/**
 * The project page — §4.3, and the answer to §1.3's success criterion.
 *
 * "A staff member other than the builder navigates to a live project, reads its
 * status and its most recent notes, and never opens a screen whose subject is
 * the permission model."
 *
 * Two things make that possible and both are here:
 *
 * **One tree.** The internal space and its shared child render together, with
 * published items badged as visible to the client. There is no internal tab and
 * no client tab, because there is no boundary to perceive: the shared space is a
 * *child* of the internal one specifically so staff see both, and the badge is a
 * fact about where a node sits rather than a filter this screen applies.
 *
 * **Recent activity.** "The latest meeting notes" is a recency question, and a
 * tree ordered by `position` cannot answer it. Phase 0 §6.3 named the project
 * timeline as one of the event log's four consumers and nothing read it that way
 * until now.
 */

import { Link, useParams } from '@tanstack/react-router';
import { useProject, useProjectActivity } from '../../api/queries.ts';
import type { ProjectTreeNode } from '../../api/types.ts';
import { formatDate, formatRelative } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { ErrorState, Loading, PageError } from '../../components/ui/feedback.tsx';
import {
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  Mono,
  PageHeader,
} from '../../components/ui/layout.tsx';

const STATUS_TONES = {
  active: 'success',
  on_hold: 'warning',
  complete: 'gray',
  archived: 'gray',
} as const;

/** Nests the flat list the API returns, preserving its order at every level. */
function nest(nodes: ProjectTreeNode[]): Array<{ node: ProjectTreeNode; depth: number }> {
  const children = new Map<string | null, ProjectTreeNode[]>();
  const known = new Set(nodes.map((node) => node.id));
  for (const node of nodes) {
    // A node whose parent is not in this list is a root as far as this tree is
    // concerned — which happens when the parent is one the reader cannot see.
    const parent = node.parentNodeId && known.has(node.parentNodeId) ? node.parentNodeId : null;
    const bucket = children.get(parent) ?? [];
    bucket.push(node);
    children.set(parent, bucket);
  }

  const out: Array<{ node: ProjectTreeNode; depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    for (const node of children.get(parent) ?? []) {
      out.push({ node, depth });
      walk(node.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function ProjectDetailPage() {
  const { projectId } = useParams({ from: '/_authed/_employee/projects/$projectId' });
  const query = useProject(projectId);
  const activity = useProjectActivity(projectId);

  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Project"
        back={{ href: '/projects', label: 'Back to projects' }}
      />
    );
  }

  const { project, space, sharedSpace, organization, tree, members } = query.data;
  const rows = nest(tree);

  return (
    <>
      <PageHeader title={project.name} description={organization.name} />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" padded={false}>
          <CardHeader
            title="Contents"
            description="The project's own space and the portal home beneath it, as one tree. Badged items are the ones the client can see."
          />
          <div className="p-5">
            {rows.length === 0 ? (
              <p className="text-sm text-tertiary">
                Nothing here yet — or nothing here that you can reach. This tree is the same
                resolution the rest of the system uses, not a filtered copy of it.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {rows.map(({ node, depth }) => (
                  <li
                    key={node.id}
                    className="flex flex-wrap items-center gap-2 text-sm"
                    style={{ paddingLeft: `${depth * 1.25}rem` }}
                  >
                    {node.type === 'document' ? (
                      <Link
                        to="/documents/$nodeId"
                        params={{ nodeId: node.id }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {node.title}
                      </Link>
                    ) : (
                      <span className="font-medium text-primary">{node.title}</span>
                    )}
                    {node.sharedWithClient ? <Badge tone="brand">Shared with client</Badge> : null}
                    {node.status && node.status !== 'published' ? (
                      <Badge tone="outline">{node.status.replace('_', ' ')}</Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <h2 className="mb-4 text-md font-semibold text-primary">Status</h2>
            <DefinitionList>
              <Definition term="Status">
                <Badge tone={STATUS_TONES[project.status]}>
                  {project.status.replace('_', ' ')}
                </Badge>
              </Definition>
              <Definition term="Client">
                <Link
                  to="/organizations/$organizationId"
                  params={{ organizationId: organization.id }}
                >
                  {organization.name}
                </Link>
              </Definition>
              <Definition term="Runs">
                {formatDate(project.startsOn)} — {project.endsOn ? formatDate(project.endsOn) : '—'}
              </Definition>
              <Definition term="Space">
                <Link to="/spaces/$spaceId" params={{ spaceId: space.id }}>
                  {space.name}
                </Link>
              </Definition>
              {sharedSpace ? (
                <Definition term="Portal home">
                  <Link to="/spaces/$spaceId" params={{ spaceId: sharedSpace.id }}>
                    {sharedSpace.name}
                  </Link>
                </Definition>
              ) : null}
              <Definition term="Identifier">
                <Mono>{project.id}</Mono>
              </Definition>
            </DefinitionList>
          </Card>

          <Card padded={false}>
            <CardHeader
              title="Who is on this"
              description="For assignment and filtering. Not access — that is a grant on the space."
            />
            <div className="p-5">
              {members.length === 0 ? (
                <p className="text-sm text-tertiary">Nobody recorded yet.</p>
              ) : (
                <ul className="flex flex-col gap-2 text-sm">
                  {members.map((member) => (
                    <li key={member.personId} className="flex flex-wrap items-center gap-2">
                      <Link to="/admin/people/$personId" params={{ personId: member.personId }}>
                        {member.name}
                      </Link>
                      <Badge tone="outline">{member.role.replace('_', ' ')}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Card padded={false}>
        <CardHeader
          title="Recent activity"
          description="Newest first, from the activity log. The tree is ordered by position and cannot answer 'what changed last'."
        />
        <div className="p-5">
          {activity.isError ? (
            <ErrorState error={activity.error} />
          ) : activity.isPending ? (
            <p className="text-sm text-tertiary">Loading…</p>
          ) : activity.data.activity.length === 0 ? (
            <p className="text-sm text-tertiary">Nothing has happened here yet.</p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {activity.data.activity.map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-center gap-2">
                  <span className="text-tertiary">{formatRelative(entry.createdAt)}</span>
                  <Badge tone="outline" mono>
                    {entry.type}
                  </Badge>
                  {entry.title ? (
                    <Link to="/documents/$nodeId" params={{ nodeId: entry.subjectId }}>
                      {entry.title}
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </>
  );
}
